import { ReactElement, useEffect, useState } from 'react';
import clsx from 'clsx';
import {
  DashboardDataFilter,
  DeviceAttributesV1,
  DeviceType,
  ParametersType,
  PriorityList
} from '../constants/telosair-interfaces';
import { getPriorityColor } from '../_helpers/dashboardHelper';
import { classesBasedOnPriority, getColorForMap } from '../_helpers/globalHelper';
import DeviceHoverCard from './DeviceHoverCard';
import { useUnifiedData } from '../hooks/useUnifiedData';

type DeviceHoverProps = {
  deviceData: DeviceAttributesV1;
  heatMap?: boolean;
  size: string;
  imageDisplayData: {
    height: number;
    width: number;
    offsetLeft: number;
    offsetTop: number;
  };
  selectedParameters: ParametersType[];
  selectedDevice?: number | null;
  shouldHighlight?: boolean;
  noFloorMap?: boolean;
  setDataFilter?: (filter: DashboardDataFilter | null) => void;
};

type DeviceHoverState = {
  x: number;
  y: number;
};

/**
 * DeviceHover component renders circle icon where the device has located and on hover renders device sensors data.
 * @param props - Properties of DeviceHover Component.
 * @returns - A React Component.
 */
export default function DeviceHover(props: DeviceHoverProps): ReactElement {
  const {
    deviceData,
    size,
    imageDisplayData,
    heatMap,
    selectedParameters,
    selectedDevice,
    shouldHighlight,
    noFloorMap,
    setDataFilter
  } = props;

  const {
    clientThresholdSettings,
    deviceDataSummary,
    loading,
    allAlerts,
    userConfigDataV2,
    devices
  } = useUnifiedData();
  const [priority, setPriority] = useState<PriorityList>(PriorityList.none);
  const [hovered, setHovered] = useState<DeviceHoverState | null>(null);
  const particleData = deviceDataSummary[deviceData.serial_number]?.parameters || {};
  /**
   * GetSize for Device Icon returns the size of the device (circle) Icon on the bases of the size.
   * @returns - Classes.
   */
  const getSizeForDeviceIcon = (): string => {
    if (shouldHighlight && priority === PriorityList.high) {
      return 'animate-ping hover:bg-opacity-90';
    }
    if (size === 'sm') {
      return 'hover:bg-opacity-90';
    }
    if (size === 'md') {
      return '';
    }
    return '';
  };

  useEffect(() => {
    const priorityList: PriorityList[] = [];
    Object.entries(particleData)
      .filter(([key]) =>
        selectedParameters.length ? selectedParameters?.includes(key as ParametersType) : true
      )
      .forEach(([key, value]) => {
        if (value !== null) {
          const particlePriority = getPriorityColor(
            key as ParametersType,
            value,
            clientThresholdSettings,
            userConfigDataV2,
            devices.find((device) => device.serialNumber === deviceData.serial_number)
              ?.deviceType || DeviceType.MIXED
          );
          priorityList.push(particlePriority);
        } else {
          priorityList.push(PriorityList.none);
        }
      });
    if (priorityList.includes(PriorityList.high)) {
      setPriority(PriorityList.high);
      return;
    }
    if (priorityList.includes(PriorityList.medium)) {
      setPriority(PriorityList.medium);
      return;
    }
    if (priorityList.includes(PriorityList.low)) {
      setPriority(PriorityList.low);
      return;
    }
    setPriority(PriorityList.none);
  }, [
    particleData,
    selectedParameters,
    devices,
    deviceData.serial_number,
    clientThresholdSettings,
    userConfigDataV2
  ]);

  const alert = allAlerts.filter(
    (item) =>
      parseInt(item.serialNumber, 10) === deviceData.serial_number && item.status === 'pending'
  );

  return (
    <div
      style={
        !noFloorMap
          ? {
              top: deviceData.y * imageDisplayData.height + imageDisplayData.offsetTop - 2,
              left: deviceData.x * imageDisplayData.width + imageDisplayData.offsetLeft - 2
            }
          : {}
      }
      className={clsx({
        absolute: !noFloorMap
      })}
    >
      <div className="group cursor-pointer relative">
        <div
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setDataFilter?.({
                type: 'device',
                id: deviceData.serial_number,
                name: deviceData.deviceName
              });
            }
          }}
          onClick={() => {
            setDataFilter?.({
              type: 'device',
              id: deviceData.serial_number,
              name: deviceData.deviceName
            });
          }}
          onMouseEnter={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const hoverCardHeight = 500;
            const hoverCardWidth = 200; // Adjusted to match min-w-[11.25rem] + padding
            const windowHeight = window.innerHeight;
            const windowWidth = window.innerWidth;

            // Find the parent container
            const parentContainer = e.currentTarget.closest('.map-container') || document.body;
            const parentRect = parentContainer.getBoundingClientRect();

            // Calculate initial position
            let yPosition = rect.top + 25;
            let xPosition = rect.left + 25;

            // Adjust Y position to stay within parent container and window
            const maxY = Math.min(windowHeight, parentRect.bottom) - hoverCardHeight - 10;
            const minY = Math.max(0, parentRect.top + 10);

            if (maxY < minY) {
              yPosition = minY;
            } else {
              if (yPosition > maxY) {
                yPosition = maxY;
              }
              if (yPosition < minY) {
                yPosition = minY;
              }
            }

            // Adjust X position to stay within parent container and window
            const maxX = Math.min(windowWidth, parentRect.right) - hoverCardWidth - 10;
            const minX = Math.max(0, parentRect.left + 10);

            if (maxX < minX) {
              xPosition = minX;
            } else {
              if (xPosition > maxX) {
                xPosition = maxX;
              }
              if (xPosition < minX) {
                xPosition = minX;
              }
            }

            setHovered({
              y: yPosition,
              x: xPosition
            });
          }}
          onMouseLeave={() => setHovered(null)}
          className={`group-hover:scale-90 rounded-full material-icons-outlined scale-75  relative ${
            selectedDevice === deviceData.serial_number && 'border-2'
          } ${classesBasedOnPriority(priority, true)} ${getSizeForDeviceIcon()} ${
            alert.length > 0 && 'pulse-scale'
          } `}
        >
          memory
        </div>
        {hovered && (
          <div className="relative">
            <DeviceHoverCard
              loading={loading}
              deviceData={deviceData}
              particleData={particleData}
              alert={alert}
              selectedParameters={selectedParameters}
              position={hovered}
            />
          </div>
        )}
      </div>
      {heatMap && (
        <div
          className={`absolute top-[-30px] z-[0] bottom-[-30px] left-[-30px] right-[-30px] blur-lg rounded-full ${getColorForMap(
            priority
          )}`}
        />
      )}
    </div>
  );
}
